import { useEffect } from "react";
import { Link } from "react-router-dom";
import { solutions, solutionsContent } from "@/content";
import LightNavbar from "@/pages/main-light/components/LightNavbar";
import LightFooter from "@/pages/main-light/components/LightFooter";
import SolutionForm from "./components/SolutionForm";
import SeoHead from "@/components/feature/SeoHead";
import { Rich, interpolate } from "@/lib/rich";

/**
 * Решения. Один адрес `/solutions` — две темы.
 */
export default function SolutionsPage() {
  const { seo, hero, labels, bottomCta } = solutionsContent;
  const total = solutions.length;

  // Переход к якорю из адреса (например, /solutions#calc-fields)
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    const element = document.getElementById(hash.replace("#", ""));
    if (!element) return;
    setTimeout(() => {
      const top = element.getBoundingClientRect().top + window.scrollY - 96;
      window.scrollTo({ top, behavior: "smooth" });
    }, 300);
  }, []);

  const scrollToSlug = (slug: string) => {
    const element = document.getElementById(slug);
    if (!element) return;
    const top = element.getBoundingClientRect().top + window.scrollY - 96;
    window.scrollTo({ top, behavior: "smooth" });
  };

  const seoHead = (
    <SeoHead
      title={interpolate(seo.title, { count: total })}
      description={interpolate(seo.description, { count: total })}
      keywords={seo.keywords}
      canonicalPath={seo.canonicalPath}
    />
  );

  return (
    <>
      {seoHead}
      <div className="min-h-screen bg-[#FAFAF8] dark:bg-[#0A0A0A] font-sans">
        <LightNavbar />

        <section className="pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-6">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-stone-500 dark:text-white/60 hover:text-stone-900 transition-colors mb-8 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              {hero.backLabel}
            </Link>
            <h1 className="text-3xl md:text-5xl font-extrabold text-stone-900 dark:text-white mb-5 max-w-3xl">
              <Rich text={hero.title} accentClassName="text-orange-700 dark:text-orange-400" />
            </h1>
            <p className="text-stone-500 dark:text-white/60 text-lg md:text-xl leading-relaxed max-w-3xl mb-10">
              {interpolate(hero.text, { count: total })}
            </p>

            <div className="flex flex-wrap gap-2">
              {solutions.map((solution) => (
                <button
                  key={solution.slug}
                  onClick={() => scrollToSlug(solution.slug)}
                  className="px-3.5 py-1.5 rounded-full bg-stone-100 dark:bg-white/[0.06] text-stone-600 dark:text-white/65 text-xs font-semibold hover:bg-stone-200 hover:text-stone-900 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {solution.title}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="pb-24">
          <div className="max-w-7xl mx-auto px-6 space-y-8">
            {solutions.map((solution, index) => (
              <div
                key={solution.slug}
                id={solution.slug}
                className={`scroll-mt-24 rounded-3xl border border-stone-100 dark:border-white/[0.08] ${
                  index % 2 === 0 ? "bg-white dark:bg-white/[0.04]" : "bg-[#FAFAF8] dark:bg-[#0C0C0E]"
                }`}
              >
                <div className="p-8 md:p-10">
                  <div className="flex flex-col md:flex-row md:items-start gap-6">
                    <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-stone-900 dark:bg-orange-500 shrink-0">
                      <i className={`${solution.icon} text-white dark:text-[#0A0A0A] text-2xl`}></i>
                    </div>
                    <div className="flex-1">
                      <h2 className="text-2xl md:text-3xl font-extrabold text-stone-900 dark:text-white mb-3">
                        {solution.title}
                      </h2>
                      <p className="text-stone-500 dark:text-white/60 text-base md:text-lg leading-relaxed max-w-4xl">
                        {solution.desc}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="px-8 md:px-10 pb-8">
                  <h3 className="text-sm font-bold text-stone-900 dark:text-white uppercase tracking-wider mb-5">
                    {labels.features}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {solution.features.map((feature) => (
                      <div
                        key={feature}
                        className="flex items-start gap-3 bg-[#FAFAF8] dark:bg-[#0C0C0E] border border-stone-100 dark:border-white/[0.08] rounded-xl p-4"
                      >
                        <div className="w-5 h-5 flex items-center justify-center rounded-full bg-orange-600 dark:bg-orange-500 shrink-0 mt-0.5">
                          <i className="ri-check-line text-white dark:text-[#0A0A0A] text-[10px]"></i>
                        </div>
                        <p className="text-stone-700 dark:text-white/80 text-sm leading-relaxed">
                          {feature}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="px-8 md:px-10 pb-8">
                  <h3 className="text-sm font-bold text-stone-900 dark:text-white uppercase tracking-wider mb-5">
                    {labels.howItWorks}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {solution.howItWorks.map((step) => (
                      <div
                        key={step.step}
                        className="bg-white dark:bg-white/[0.04] border border-stone-100 dark:border-white/[0.08] rounded-2xl p-5 relative"
                      >
                        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-stone-900 dark:bg-orange-500 text-white dark:text-[#0A0A0A] text-sm font-bold mb-3">
                          {step.step}
                        </div>
                        <h4 className="text-sm font-bold text-stone-900 dark:text-white mb-1.5">
                          {step.title}
                        </h4>
                        <p className="text-stone-500 dark:text-white/60 text-sm leading-relaxed">
                          {step.desc}
                        </p>
                        {step.step < solution.howItWorks.length && (
                          <div className="hidden lg:block absolute top-8 -right-2 w-4 h-[1px] bg-stone-200" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="px-8 md:px-10 pb-10">
                  <div className="mb-4">
                    <p className="text-orange-700 dark:text-orange-400 text-sm font-semibold mb-1">
                      {solution.priceNote}
                    </p>
                    <p className="text-stone-500 dark:text-white/60 text-sm">{labels.days}</p>
                  </div>
                  <SolutionForm
                    solutionTitle={solution.title}
                    formId={`form-${solution.slug}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="py-20 bg-white dark:bg-white/[0.04] border-t border-stone-100 dark:border-white/[0.08]">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <h2 className="text-2xl md:text-4xl font-extrabold text-stone-900 dark:text-white mb-4">
              <Rich text={bottomCta.title} accentClassName="text-orange-700 dark:text-orange-400" />
            </h2>
            <p className="text-stone-500 dark:text-white/60 text-lg max-w-2xl mx-auto mb-8">
              {bottomCta.text}
            </p>
            <a
              href={bottomCta.cta.href}
              className="inline-block text-sm font-bold px-8 py-3.5 rounded-full bg-stone-900 dark:bg-orange-500 text-white dark:text-[#0A0A0A] hover:bg-stone-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              {bottomCta.cta.label}
            </a>
          </div>
        </section>

        <LightFooter />
      </div>
    </>
  );
}
